import { brandStorage as localStorage } from '@/utils/brandSession';
import React, { useState, useEffect } from 'react';
import { API } from '../../utils/config';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { 
  ClipboardCheck, 
  Search, 
  Check, 
  Clock,
  AlertCircle,
  ChevronRight,
  Home,
  FileCheck,
  Users,
  Building,
  GitCompare,
  Edit,
  Eye
} from 'lucide-react';


const AdminSchedeQualita = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem('hw_token');
  
  const [loading, setLoading] = useState(true);
  const [properties, setProperties] = useState([]);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all'); // all, need_ufficio, need_responsabile, can_compare

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await fetch(`${API}/api/quality-sheets/list`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (res.ok) {
          const data = await res.json();
          setProperties(data.properties || []);
        }
      } catch (error) {
        console.error('Error fetching quality sheets:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [token]);

  const getFilteredProperties = () => {
    let filtered = properties.filter(p => 
      p.property_name.toLowerCase().includes(search.toLowerCase()) ||
      p.city?.toLowerCase().includes(search.toLowerCase())
    );
    
    switch (filter) {
      case 'need_ufficio':
        return filtered.filter(p => !p.ufficio.exists);
      case 'need_responsabile':
        return filtered.filter(p => !p.responsabile.exists);
      case 'can_compare':
        return filtered.filter(p => p.can_compare);
      case 'in_progress':
        return filtered.filter(p => 
          (p.ufficio.exists && !p.ufficio.complete) || 
          (p.responsabile.exists && !p.responsabile.complete)
        );
      default:
        return filtered;
    }
  };

  const filteredProperties = getFilteredProperties();

  // Statistiche
  const stats = {
    total: properties.length,
    ufficio_complete: properties.filter(p => p.ufficio.complete).length,
    responsabile_complete: properties.filter(p => p.responsabile.complete).length,
    can_compare: properties.filter(p => p.can_compare).length
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold flex items-center gap-2">
            <ClipboardCheck className="h-6 w-6 text-blue-600" />
            Schede Qualità - Gestione
          </h1>
          <p className="text-sm text-slate-500">
            Gestisci le schede qualità di tutti gli immobili
          </p>
        </div>
      </div>

      {/* Statistiche */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="p-4 text-center">
            <Building className="h-6 w-6 mx-auto mb-1 text-blue-600" />
            <div className="text-2xl font-bold text-blue-600">{stats.total}</div>
            <div className="text-xs text-blue-700">Immobili Totali</div>
          </CardContent>
        </Card>
        <Card className="bg-amber-50 border-amber-200">
          <CardContent className="p-4 text-center">
            <Users className="h-6 w-6 mx-auto mb-1 text-amber-600" />
            <div className="text-2xl font-bold text-amber-600">{stats.ufficio_complete}</div>
            <div className="text-xs text-amber-700">Schede Ufficio</div>
          </CardContent>
        </Card>
        <Card className="bg-green-50 border-green-200">
          <CardContent className="p-4 text-center">
            <Check className="h-6 w-6 mx-auto mb-1 text-green-600" />
            <div className="text-2xl font-bold text-green-600">{stats.responsabile_complete}</div>
            <div className="text-xs text-green-700">Schede Responsabili</div>
          </CardContent>
        </Card>
        <Card className="bg-purple-50 border-purple-200">
          <CardContent className="p-4 text-center">
            <GitCompare className="h-6 w-6 mx-auto mb-1 text-purple-600" />
            <div className="text-2xl font-bold text-purple-600">{stats.can_compare}</div>
            <div className="text-xs text-purple-700">Confrontabili</div>
          </CardContent>
        </Card>
      </div>

      {/* Filtri */}
      <div className="flex flex-col md:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            placeholder="Cerca immobile..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
          />
        </div>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-full md:w-[220px]">
            <SelectValue placeholder="Filtra per stato" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutti gli immobili</SelectItem>
            <SelectItem value="need_ufficio">Manca scheda Ufficio</SelectItem>
            <SelectItem value="need_responsabile">Manca scheda Responsabile</SelectItem>
            <SelectItem value="can_compare">Pronte per confronto</SelectItem>
            <SelectItem value="in_progress">In lavorazione</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Tabella */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b">
                <tr>
                  <th className="text-left p-3 font-medium text-sm">Immobile</th>
                  <th className="text-center p-3 font-medium text-sm">Scheda Ufficio</th>
                  <th className="text-center p-3 font-medium text-sm">Scheda Responsabile</th>
                  <th className="text-center p-3 font-medium text-sm">Azioni</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {filteredProperties.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="p-8 text-center text-slate-500">
                      <FileCheck className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                      <p>Nessun immobile trovato</p>
                    </td>
                  </tr>
                ) : (
                  filteredProperties.map(prop => (
                    <tr key={prop.property_id} className="hover:bg-slate-50">
                      <td className="p-3">
                        <div className="flex items-center gap-3">
                          <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                            <Home className="h-5 w-5 text-blue-600" />
                          </div>
                          <div>
                            <div className="font-medium">{prop.property_name}</div>
                            <div className="text-sm text-slate-500">{prop.city}</div>
                          </div>
                        </div>
                      </td>
                      
                      <td className="p-3 text-center">
                        {prop.ufficio.exists ? (
                          <div className="flex flex-col items-center gap-1">
                            <Badge className={prop.ufficio.complete ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'}>
                              {prop.ufficio.complete ? (
                                <><Check className="h-3 w-3 mr-1" /> {prop.ufficio.score}%</>
                              ) : (
                                <><Clock className="h-3 w-3 mr-1" /> Bozza</>
                              )}
                            </Badge>
                            <span className="text-xs text-slate-500">{prop.ufficio.compiler?.split('@')[0]}</span>
                          </div>
                        ) : (
                          <Badge variant="outline" className="text-slate-400">
                            <AlertCircle className="h-3 w-3 mr-1" /> Mancante
                          </Badge>
                        )}
                      </td>
                      
                      <td className="p-3 text-center">
                        {prop.responsabile.exists ? (
                          <div className="flex flex-col items-center gap-1">
                            <Badge className={prop.responsabile.complete ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'}>
                              {prop.responsabile.complete ? (
                                <><Check className="h-3 w-3 mr-1" /> {prop.responsabile.score}%</>
                              ) : (
                                <><Clock className="h-3 w-3 mr-1" /> Bozza</>
                              )}
                            </Badge>
                            <span className="text-xs text-slate-500">{prop.responsabile.compiler?.split('@')[0]}</span>
                          </div>
                        ) : (
                          <Badge variant="outline" className="text-slate-400">
                            <AlertCircle className="h-3 w-3 mr-1" /> Mancante
                          </Badge>
                        )}
                      </td>
                      
                      <td className="p-3">
                        <div className="flex items-center justify-center gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => navigate(`/admin/scheda-qualita/${prop.property_id}/compila`)}
                            title="Compila scheda ufficio"
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          
                          {prop.can_compare && (
                            <Button
                              size="sm"
                              className="bg-purple-600 hover:bg-purple-700"
                              onClick={() => navigate(`/admin/scheda-qualita/${prop.property_id}/confronto`)}
                              title="Confronta schede"
                            >
                              <GitCompare className="h-4 w-4" />
                            </Button>
                          )}
                          
                          {(prop.ufficio.exists || prop.responsabile.exists) && !prop.can_compare && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => navigate(`/admin/scheda-qualita/${prop.property_id}/dettaglio`)}
                              title="Visualizza dettagli"
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminSchedeQualita;
